import { SelectionModel } from '@angular/cdk/collections';
import { formatDate } from '@angular/common';
import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { CargoDialogService } from '@cargo/services/cargo-dialog.service';
import { AdvertiserMaterial } from '@core/models/advertiser-material';
import { BackendAPI } from '@core/models/backend-api';
import { CassError } from '@core/models/cass-error';
import { AppConfigService } from '@core/services/app-config.service';
import { AppLocaleService } from '@core/services/app-locale.service';
import { Logger } from '@core/services/logger.service';
import { OrdersService } from '@core/services/orders.service';
import { MaterialPreviewDialogParams } from '@order/models/material-preview-dialog-params';
import { OrderHelperService } from '@order/services/order-helper.service';
import { LoadingOverlayService } from '@shared/services/loading-overlay.service';
import { UserAlertService } from '@shared/services/user-alert.service';
import { SubSink } from 'subsink';

@Component({
	selector: 'cass-find-material-dialog',
	templateUrl: './find-material-dialog.component.html',
	styleUrls: ['./find-material-dialog.component.css']
})
export class FindMaterialDialogComponent implements OnInit, AfterViewInit, OnDestroy {

	private readonly COMPONENT_NAME = "FindMaterialDialogComponent";

	materialForAdvertisers: AdvertiserMaterial[] = [];

	selection = new SelectionModel<AdvertiserMaterial>(false, []);

	selectedItem: AdvertiserMaterial | undefined = undefined;
	advertiserMaterialTableDataSource = new MatTableDataSource<AdvertiserMaterial>();

	@ViewChild('advertiserMaterialTable', { read: MatSort }) advertiserMaterialTableSort: MatSort | null = null;
	advertiserMaterialTableColumnDefs: any[] = [
		{
			key: 'thumbnail',
			heading: 'ORDER_BASKET.FIND_MATERIAL_DIALOG.TABLE_COLUMN.THUMBNAIL',
			displayValue: (data: AdvertiserMaterial): string => this.getThumbnailUrl(data),
			sortableValue: (data: AdvertiserMaterial): string => data.materialSequence || ""
		},
		{
			key: 'orderBasketId',
			heading: 'ORDER_BASKET.FIND_MATERIAL_DIALOG.TABLE_COLUMN.ORDER',
			displayValue: (data: AdvertiserMaterial): string => data.orderBasketId || ""
		},
		{
			key: 'lastIssueDate',
			heading: 'ORDER_BASKET.FIND_MATERIAL_DIALOG.TABLE_COLUMN.LAST_ISSUE_DATE',
			displayValue: (data: AdvertiserMaterial): string => data.lastIssueDate ? formatDate(data.lastIssueDate, this._localeService.dateFormat, this._localeService.currentOrDefaultLocaleId) : "",
			sortableValue: (data: AdvertiserMaterial): number => data.lastIssueDate ? (new Date(data.lastIssueDate)).valueOf() : 0
		},
		{
			key: 'campaignName',
			heading: 'ORDER_BASKET.FIND_MATERIAL_DIALOG.TABLE_COLUMN.CAMPAIGN_NAME',
			displayValue: (data: AdvertiserMaterial): string => data.campaignName || ""
		},
	];

	readonly initialAdvertiserMaterialTableDisplayedColumns = ['select', ...this.advertiserMaterialTableColumnDefs.map(x => x.key)];

	advertiserMaterialTableDisplayedColumns = [...this.initialAdvertiserMaterialTableDisplayedColumns];

	protected readonly backendApi = BackendAPI;

	isLoading: boolean = true;
	isDataReady: boolean = false;

	isRepeatWithChangesEnabled = false;

	private _subs = new SubSink();

	constructor (
		@Inject(MAT_DIALOG_DATA) private _materialPreviewParams: MaterialPreviewDialogParams,
		private _dialogRef: MatDialogRef<FindMaterialDialogComponent>,
		private _alertService: UserAlertService,
		private _appConfigService: AppConfigService,
		private _cargoDialogService: CargoDialogService,
		private _loadingOverlay: LoadingOverlayService,
		private _localeService: AppLocaleService,
		private _logger: Logger,
		private _orderHelperService: OrderHelperService,
		private _orderService: OrdersService
	) {
		if (!this._materialPreviewParams) {
			this._logger.error(this.COMPONENT_NAME, "Mandatory material preview parameter is not passed to the dialog");
		}

		this._subs.add(
			this._appConfigService.isCargoEnabled$.subscribe((isCargoEnabled: boolean) => {
				if (isCargoEnabled) {
					// this.isRepeatWithChangesEnabled = true; // <-- DISABLED until the spec and Cargo are ready
					this._logger.debug(this.COMPONENT_NAME, "Init with Cargo enabled");
				}
				else {
					this._logger.debug(this.COMPONENT_NAME, "Init with Cargo disabled");
				}
			}));
	}

	ngOnInit(): void {
		// request all materials used by the advertiser, considering given width and depth
		this._orderService.getMaterialForAdvertiser(this._materialPreviewParams.advertiserId, this._materialPreviewParams.orderAdDetail.width, this._materialPreviewParams.orderAdDetail.depth)
			.subscribe({
				next: (advertiserMaterials: AdvertiserMaterial[]) => {
					this.isLoading = false;
					if (advertiserMaterials && advertiserMaterials.length > 0) {
						this.isDataReady = true;
						this.materialForAdvertisers = advertiserMaterials.filter((advertiserMaterials: AdvertiserMaterial) => {
							return advertiserMaterials.materialSequence &&
								advertiserMaterials.orderBasketId &&
								advertiserMaterials.orderId &&
								advertiserMaterials.lastIssueDate;
						});

						if (this.materialForAdvertisers.length !== advertiserMaterials.length) {
							this._logger.warning(this.COMPONENT_NAME, `getMaterialForAdvertiser() returned items with empty mandatory fields for advertiserId="${this._materialPreviewParams.advertiserId}", width=${this._materialPreviewParams.orderAdDetail.width}, depth=${this._materialPreviewParams.orderAdDetail.depth}`);
						}

						this.updateAdvertiserMaterialDataSource(this.materialForAdvertisers);
					}
				},
				error: (err: CassError) => {
					this.isLoading = false;
					this._alertService.errorByCassError(err);
					this.closeDialog();
				},
			});
	}

	ngAfterViewInit(): void {
		this.updateAdvertiserMaterialDataSource(this.materialForAdvertisers);
	}

	ngOnDestroy(): void {
		this._subs.unsubscribe();
	}

	closeDialog() {
		this._dialogRef.close();
	}

	selectItem(row: AdvertiserMaterial) {
		this.selection.toggle(row);
		this.selectedItem = row;
	}

	save() {
		if (!this.selectedItem) {
			return;
		}

		this._loadingOverlay.showIndeterminateHorizontalProgressIndicator();

		this._orderService.updateOrderMaterialFromPreviousOrders(
			this._materialPreviewParams.orderId,
			this._materialPreviewParams.orderAdDetail.materialId,
			this.selectedItem.orderBasketId || "",
			this.selectedItem.materialSequence || ""
		)
			.subscribe({
				next: (_) => {
					this._orderHelperService.changeMaterialPanelReloadStatus(Math.random());
					this.closeDialog();
					//this.openMaterialPreviewDialog();
					this._loadingOverlay.hide();
				},
				error: (err: CassError) => {
					this._loadingOverlay.hide();
					this._alertService.errorByCassError(err);
				}
			});
	}

	repeatWithChanges(): void {

		this._logger.warning(this.COMPONENT_NAME, "repeatWithChanges() is not supported and should be hidden in UI");

		// We cannot support repeat with changes correctly until we discuss with Laidback the Cargo side of the solution.
		// We need to pass the order number and material sequence of the repeated material along with the new order number,
		// so that they can load up the old material assets and copy it forward into a new inhouse production request.
		// None of that exists currently in the forms. Cargo may also require the old job number(their system ID) and we don't store that currently in our system.


		//	this.closeDialog();

		// Copy the material first

		//	this._cargoDialogService.displayProductionRequestDialog(
		//		"ORDER_BASKET.NEW_MATERIAL_OPTIONS_DIALOG.WE_WILL_DESIGN_IT_OPTION.REPEAT_WITH_CHANGES_OPTION",
		//        this._materialPreviewParams.orderAdDetail.orderId, this._materialPreviewParams.orderAdDetail.materialId, this._materialPreviewParams.orderAdDetail.width, this._materialPreviewParams.orderAdDetail.depth)
		//		.subscribe({
		//			next: (cargoDialogResult) => {
		//				console.log("TBD: process cargoDialogResult: ", cargoDialogResult);
		//			}
		//		});
	}

	private getThumbnailUrl(data: AdvertiserMaterial) {
		return this.backendApi.orderMaterial + '/' + data.orderBasketId + '/' + data.materialSequence + '/thumbnail';
	}

	private updateAdvertiserMaterialDataSource(advertiserMaterial: AdvertiserMaterial[]) {

		this.advertiserMaterialTableDataSource.data = advertiserMaterial;

		this.advertiserMaterialTableDataSource.sortingDataAccessor = (item: AdvertiserMaterial, sortHeaderId: string) => {
			const columnDef = this.advertiserMaterialTableColumnDefs.find(x => x.key === sortHeaderId);
			if (columnDef) {
				return columnDef.sortableValue ? columnDef.sortableValue(item) : columnDef.displayValue ? columnDef.displayValue(item) : (item as any)[sortHeaderId] ?? '';
			}
		};

		setTimeout(() => {
			if (this.advertiserMaterialTableDataSource) {
				this.advertiserMaterialTableDataSource.sort = this.advertiserMaterialTableSort;

				this.advertiserMaterialTableDataSource.sort?.sort({
					id: 'orderBasketId', start: 'desc',
					disableClear: true
				});
			}
		}, 0);
	}
}
